import { flatten } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQueryFetch } from 'sonata-react-components';

import { useTargetingContext } from '@/contexts/Targeting/TargetingContext';
import { getAudienceSegments } from '@/resources/audienceTargeting.resources';
import { FormExpandableSection } from '../../common/FormExpandableSection';
import { AudienceListHeader } from './AudienceListHeader';
import { AudienceTargetingGroupList } from './AudienceTargetingGroupList';
import { AudienceTargetingList } from './AudienceTargetingList';
import { ExcludeRetargetingPrompt } from './ExcludeRetargetingPrompt';
import { ensureActiveIncludedGroup, isExcludingIncludedRetargeting } from './helpers/audienceGroups.helper';
import { getAudienceTargetingTags } from './helpers/getSectionHeaderTags.helper';
import { RetargetingShortcut } from './RetargetingShortcut';

import type { NormalizedUserAudienceSegment } from 'sonata-static-storage/lib/common/common.types';
import type { BaseCurrencyInterface } from 'sonata-static-storage/lib/schemas/baseCurrency/baseCurrency.schema';
import type { AudienceWithRetargetingType } from 'sonata-static-storage/lib/schemas/targeting/Audience/Audience.types';
import type { TargetingBaseType } from 'sonata-static-storage/lib/schemas/targeting/TargetingBaseType';
import type { RetargetingSelection } from './RetargetingShortcut/RetargetingShortcut';

import './audienceTargeting.styles.scss';

export type AudienceGroup = TargetingBaseType['audience']['lists'][number];

type State = {
  audienceSegmentsGroups: AudienceGroup[];
  // Segments waiting for the user to confirm they exclude the retargeting
  pendingExcludedIds: string[] | null;
};

type Props = {
  baseCurrency: BaseCurrencyInterface;
};

const EMPTY_GROUP_INCLUDED: AudienceGroup = {
  active: true,
  distribution: 0,
  id: '0',
  logic: 'AND',
  name: 'Audience List',
  segmentIds: [],
  segments: [],
  type: 'included'
};

const EXCLUDED_GROUP: AudienceGroup = {
  active: false,
  distribution: 0,
  id: '0',
  logic: 'OR',
  name: 'Audience List',
  segmentIds: [],
  segments: [],
  type: 'excluded'
};

const DEFAULT_STATE_VALUE: State = {
  audienceSegmentsGroups: [],
  pendingExcludedIds: null
};

export function AudienceTargeting({ baseCurrency }: Props) {
  const [reloadKey, setReloadKey] = useState(0);
  const { formatMessage } = useIntl();
  const token: Record<string, any> = {};

  const { setAudience, setRetargetingSegment, resetSection, targeting, user } = useTargetingContext();

  const audience: AudienceWithRetargetingType = targeting.audience;

  const { data: audienceSegments, isLoading } = useQueryFetch(['audienceSegments', reloadKey], async () =>
    getAudienceSegments(token, user)
  );

  const [{ audienceSegmentsGroups, pendingExcludedIds }, setState] = useState<State>(DEFAULT_STATE_VALUE);

  const segmentsIdsInUse = flatten(audience?.lists?.map(list => list.segmentIds)) || [];

  const pendingListId = audience?.retargetingSegment?.listId;
  const isGroupKept = (group: AudienceGroup) => group.segmentIds.length > 0 || group.id === pendingListId;

  const { isLoading: isLoadingSegments, refetch: refetchSegmentsGroups } = useQueryFetch(
    ['audienceSegmentsIds'],
    async () => {
      const selectedSegment = segmentsIdsInUse.length
        ? await getAudienceSegments(token, user, segmentsIdsInUse)
        : undefined;
      const segmentsGroups = audience?.lists?.map(list => {
        return {
          ...list,
          segments:
            selectedSegment?.items?.filter((segment: Record<string, any>) =>
              list.segmentIds.includes(segment.segmentId)
            ) || []
        };
      });
      setState(prevState => ({
        ...prevState,
        audienceSegmentsGroups: segmentsGroups || []
      }));
    },
    {
      // The query only fills the local groups state, which is lost on unmount: never serve it from cache
      cacheTime: 0,
      enabled: segmentsIdsInUse.length > 0 || Boolean(pendingListId)
    }
  );

  const updateAudienceSegmentsGroups = (
    type: string,
    selectedSegments: NormalizedUserAudienceSegment[],
    currentGroups: AudienceGroup[]
  ): AudienceGroup[] => {
    const updatedGroups = [...currentGroups];
    selectedSegments.forEach(selectedSegment => {
      if (type === 'excluded') {
        updatedGroups.forEach(group => {
          if (group.type === 'included') {
            group.segmentIds = group.segmentIds.filter(segmentId => segmentId !== selectedSegment.segmentId);
            group.segments = group.segments?.filter(segment => segment.segmentId !== selectedSegment.segmentId);
          }
        });
        const excludedGroup = updatedGroups.find(group => group.type === 'excluded');
        if (excludedGroup) {
          excludedGroup.segmentIds = [...new Set([...excludedGroup.segmentIds, selectedSegment.segmentId])];
          if (!excludedGroup.segments?.some(segment => segment.segmentId === selectedSegment.segmentId)) {
            excludedGroup.segments = [...(excludedGroup.segments || []), selectedSegment];
          }
        } else {
          updatedGroups.push({
            ...EXCLUDED_GROUP,
            id: new Date().getTime().toString(),
            segmentIds: [selectedSegment.segmentId],
            segments: [selectedSegment]
          });
        }
      }

      if (type === 'included') {
        const includedGroup = updatedGroups.filter(group => group.type === 'included');
        const selectedIncludedGroup = includedGroup.find(group => group.active) || includedGroup[0];

        // A segment lives in a single group: including it moves it from the excluded or any other included group
        updatedGroups.forEach(group => {
          if (group !== selectedIncludedGroup) {
            group.segmentIds = group.segmentIds.filter(segmentId => segmentId !== selectedSegment.segmentId);
            group.segments = group.segments?.filter(segment => segment.segmentId !== selectedSegment.segmentId);
          }
        });

        if (selectedIncludedGroup) {
          selectedIncludedGroup.segmentIds = [
            ...new Set([...selectedIncludedGroup.segmentIds, selectedSegment.segmentId])
          ];
          // Compared by id: segments fetched for the saved lists are not the same objects as the list ones
          if (!selectedIncludedGroup.segments?.some(segment => segment.segmentId === selectedSegment.segmentId)) {
            selectedIncludedGroup.segments = [...(selectedIncludedGroup.segments || []), selectedSegment];
          }
        } else {
          updatedGroups.unshift({
            ...EMPTY_GROUP_INCLUDED,
            id: new Date().getTime().toString(),
            segmentIds: [selectedSegment.segmentId],
            segments: [selectedSegment]
          });
        }
      }
    });

    const updatedGroupsWithoutEmptyGroup = ensureActiveIncludedGroup(updatedGroups.filter(isGroupKept));
    const groupsToSave = updatedGroupsWithoutEmptyGroup.map(({ segments, ...rest }) => rest);
    setAudience(groupsToSave);
    return updatedGroupsWithoutEmptyGroup;
  };

  const getSelectedSegments = (ids: string[]): NormalizedUserAudienceSegment[] | undefined =>
    audienceSegments?.items?.filter((segment: Record<string, any>) => ids.includes(segment.id));

  const applyIncludeExclude = (ids: string[], type: string) => {
    const selectedSegments = getSelectedSegments(ids);

    if (!selectedSegments) {
      return;
    }

    setState(prevState => {
      const updatedGroups = updateAudienceSegmentsGroups(type, selectedSegments, prevState.audienceSegmentsGroups);
      return {
        ...prevState,
        audienceSegmentsGroups: updatedGroups
      };
    });
  };

  const handleIncludeExcludeButton = (ids: string[], type: string) => {
    const selectedSegments = getSelectedSegments(ids);
    if (
      type === 'excluded' &&
      selectedSegments &&
      isExcludingIncludedRetargeting(selectedSegments, audienceSegmentsGroups)
    ) {
      setState(prevState => ({ ...prevState, pendingExcludedIds: ids }));
      return;
    }
    applyIncludeExclude(ids, type);
  };

  const handleExcludePromptRetargetingCancel = () => {
    setState(prevState => ({ ...prevState, pendingExcludedIds: null }));
  };

  const handleExcludePromptRetargetingConfirm = () => {
    if (pendingExcludedIds) {
      applyIncludeExclude(pendingExcludedIds, 'excluded');
    }
    handleExcludePromptRetargetingCancel();
  };

  const handleNewGroupChange = () => {
    const newGroup = {
      ...EMPTY_GROUP_INCLUDED,
      id: new Date().getTime().toString()
    };
    const updatedGroup = audienceSegmentsGroups.map(group => {
      if (group.active) {
        return {
          ...group,
          active: false
        };
      }
      return group;
    });
    setState(prevState => ({
      ...prevState,
      audienceSegmentsGroups: [newGroup, ...updatedGroup]
    }));
  };

  const handleSelectedGroup = (id: string) => {
    const updatedGroup = audienceSegmentsGroups.map(group => ({
      ...group,
      active: group.id === id
    }));
    setState(prevState => ({
      ...prevState,
      audienceSegmentsGroups: updatedGroup
    }));
  };

  const handleClearRetargeting = () => {
    const listId = audience?.retargetingSegment?.listId;
    if (!listId) {
      return;
    }
    const remainingGroups = audienceSegmentsGroups.filter(group => group.id !== listId || group.segmentIds.length > 0);
    setState(prevState => ({ ...prevState, audienceSegmentsGroups: remainingGroups }));
    const groupsToSave = remainingGroups
      .filter(group => group.segmentIds.length > 0)
      .map(({ segments, ...rest }) => rest);
    setRetargetingSegment(null, groupsToSave);
  };

  const handleRetargetingChange = (selection: RetargetingSelection | null) => {
    if (!selection) {
      handleClearRetargeting();
      return;
    }
    let groups = audienceSegmentsGroups;
    let targetGroup =
      groups.find(group => group.active && group.type === 'included') ||
      groups.find(group => group.type === 'included');
    if (!targetGroup) {
      targetGroup = { ...EMPTY_GROUP_INCLUDED, id: new Date().getTime().toString() };
      groups = [targetGroup, ...groups];
    }
    const listId = targetGroup.id;
    setState(prevState => ({ ...prevState, audienceSegmentsGroups: groups }));
    const groupsToSave = groups
      .filter(group => group.segmentIds.length > 0 || group.id === listId)
      .map(({ segments, ...rest }) => rest);
    setRetargetingSegment({ ...selection, listId }, groupsToSave);
  };

  const handleDeleteGroupChange = (id: string) => {
    const updatedGroups = ensureActiveIncludedGroup(audienceSegmentsGroups.filter(group => group.id !== id));
    setState(prevState => ({
      ...prevState,
      audienceSegmentsGroups: updatedGroups
    }));

    const groupsToSave = updatedGroups.map(({ segments, ...rest }) => rest);
    if (id === pendingListId) {
      setRetargetingSegment(null, groupsToSave);
      return;
    }
    setAudience(groupsToSave);
  };

  const handleDeleteSegmentChange = (id: string, groupId: string) => {
    const newGroupList = ensureActiveIncludedGroup(
      audienceSegmentsGroups
        .map(group => {
          if (group.id === groupId) {
            return {
              ...group,
              segmentIds: group.segmentIds.filter(segmentId => segmentId !== id),
              segments: group.segments?.filter(segment => segment.segmentId !== id)
            };
          }
          return group;
        })
        .filter(isGroupKept)
    );
    setState(prevState => ({
      ...prevState,
      audienceSegmentsGroups: newGroupList
    }));

    const groupsToSave = newGroupList.map(({ segments, ...rest }) => rest);
    setAudience(groupsToSave);
  };

  const handleResetAudience = () => {
    setState(DEFAULT_STATE_VALUE);
    resetSection('audienceTargeting');
  };

  // After saving, the backend injects the created segment id into the lists and drops the pending segment:
  // reload the segments so the real one shows up.
  const hadRetargetingSegment = useRef(Boolean(audience?.retargetingSegment));
  useEffect(() => {
    const hasRetargetingSegment = Boolean(audience?.retargetingSegment);
    const localIds = flatten(audienceSegmentsGroups.map(group => group.segmentIds));
    if (
      hadRetargetingSegment.current &&
      !hasRetargetingSegment &&
      segmentsIdsInUse.some(id => !localIds.includes(id))
    ) {
      setReloadKey(prevReloadKey => prevReloadKey + 1);
      refetchSegmentsGroups();
    }
    hadRetargetingSegment.current = hasRetargetingSegment;
  }, [audience?.retargetingSegment, segmentsIdsInUse.join(',')]);

  const selectedGroup = useMemo(() => {
    return audienceSegmentsGroups?.find(group => group.active) || null;
  }, [audienceSegmentsGroups]);

  const isSectionOpened = audience?.lists?.length >= 1;
  const audienceTags = getAudienceTargetingTags(audience?.lists);

  return (
    <div className="audienceTargetingSection" id="audienceTargeting">
      <FormExpandableSection
        defaultOpen={isSectionOpened}
        onReset={handleResetAudience}
        preffixId="audienceTargeting"
        tags={audienceTags}
        title={formatMessage({
          defaultMessage: 'Audience Targeting',
          id: 'targeting.audienceTargeting.title'
        })}
        tooltipText={formatMessage({
          defaultMessage:
            'Include or exclude specific user segments and custom audience lists based on behaviors and interests.',
          id: 'targeting.audienceTargeting.tooltip'
        })}
      >
        <RetargetingShortcut onChange={handleRetargetingChange} segment={audience?.retargetingSegment} />
        <AudienceListHeader onAddAudienceProfile={() => setReloadKey(prevReloadKey => prevReloadKey + 1)} />
        <div className="audienceTargetingSection__container">
          <AudienceTargetingList
            audienceSegments={audienceSegments?.items}
            audienceSegmentsGroups={audienceSegmentsGroups}
            baseCurrency={baseCurrency}
            isLoading={isLoading && isLoadingSegments}
            onClickButton={handleIncludeExcludeButton}
            selectedGroup={selectedGroup}
          />
          <AudienceTargetingGroupList
            groupsList={audienceSegmentsGroups}
            isLoading={isLoading && isLoadingSegments}
            onDeleteGroupChange={handleDeleteGroupChange}
            onDeleteRetargetingSegment={handleClearRetargeting}
            onDeleteSegmentChange={handleDeleteSegmentChange}
            onNewGroupChange={handleNewGroupChange}
            onSelectedGroup={handleSelectedGroup}
            retargetingSegment={audience?.retargetingSegment}
            selectedGroup={audienceSegmentsGroups?.find(group => group.active)?.id || null}
          />
        </div>
      </FormExpandableSection>
      {pendingExcludedIds && (
        <ExcludeRetargetingPrompt
          onCancel={handleExcludePromptRetargetingCancel}
          onConfirm={handleExcludePromptRetargetingConfirm}
        />
      )}
    </div>
  );
}
